/**
 * FE#444 — "Schedule again": turn a message that already went out into a NEW pending one.
 *
 * A sent message is a record, not a plan (see `isSentRecord`): it is soft-deleted, carries the
 * WhatsApp key of its delivery, and must never be saved back (that would queue a second send of
 * the original). So a re-run is a brand-new document built by PICKING the fields that describe
 * WHAT to send and WHERE — never by copying the record and deleting what we remember to. A field
 * added to the schema later therefore stays out of the copy until someone decides it belongs.
 */
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import type { ExtendedUserMessage } from '@common/models/extend-user-message';
import { isSentRecord } from '@common/services/planner-filters';

/** What `scheduleMessageTool` takes for a create: no `_id`. */
export type ScheduleAgainDraft = Omit<ExtendedUserMessage, '_id'>;

/**
 * The due date of the copy: the picked DAY with the ORIGINAL time of day.
 * `'past'` when that moment is not in the future, or when the original has no usable date
 * (no time of day to keep) — the caller must not schedule it.
 */
export function resolveScheduleAgainDueDate(
    originalDue: UserMessage['dueDate'] | string | undefined,
    day: Date,
    now: Date,
): Date | 'past' {
    if (originalDue === undefined) return 'past';
    const original = new Date(originalDue);
    if (Number.isNaN(original.getTime())) return 'past';
    const due = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        original.getHours(),
        original.getMinutes(),
        0,
        0,
    );
    return due.getTime() > now.getTime() ? due : 'past';
}

/** A sent record can be re-run when it was a delivered one-off to a recipient (person, group or community). */
export function isScheduleAgainEligible(message: UserMessage): boolean {
    return isSentRecord(message)
        && !message.campaignId
        && message.isRecurring !== true
        && (message.contactList?.length ?? 0) > 0;
}

/** The new pending message. Media keeps its existing `asset.url` — nothing is re-uploaded. */
export function buildScheduleAgainDraft(
    sent: UserMessage,
    dueDate: Date,
    clientIdempotencyKey: string,
): ScheduleAgainDraft {
    return {
        message: sent.message,
        mentions: sent.mentions ? [...sent.mentions] : undefined,
        // A text message can be stored with `asset: {url: ''}`; that is not media, so it is not carried over.
        ...(sent.asset?.url ? { asset: { ...sent.asset } } : {}),
        linkPreview: sent.linkPreview ? { ...sent.linkPreview } : sent.linkPreview,
        sendLinkPreview: sent.sendLinkPreview,
        isPoll: sent.isPoll,
        poll: sent.poll,
        useSpintax: sent.useSpintax,
        verifyContact: sent.verifyContact,
        contactList: sent.contactList?.map(contact => ({ ...contact })),
        dueDate,
        status: 'pending',
        clientIdempotencyKey,
    };
}

// ─── running a batch ─────────────────────────────────────────────────────────

/** `unknown` = the check itself could not decide (offline, CORS): the send path will find out. */
export type AssetProbeResult = 'ok' | 'gone' | 'unknown';

/**
 * The idempotency key of one copy: derived from WHAT is copied, WHICH day it was given and WHICH
 * submit asked for it. The backend collapses a create that repeats a key onto the first one, so a
 * repeated request of the SAME submit (double click, retry) cannot schedule a message twice.
 *
 * The submit nonce is what keeps a later, deliberate re-run working: the backend's dedupe looks the
 * key up without regard to `deleted`, so without it "schedule again for day D" after deleting that
 * copy would hand back the soft-deleted doc and leave nothing pending.
 */
export function scheduleAgainIdempotencyKey(sentId: string, day: Date, submitNonce: string): string {
    const mm = String(day.getMonth() + 1).padStart(2, '0');
    const dd = String(day.getDate()).padStart(2, '0');
    return `sa_${sentId}_${day.getFullYear()}${mm}${dd}_${submitNonce}`;
}

/** A fresh nonce for one submit of the dialog. */
export function newScheduleAgainNonce(): string {
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/**
 * How many pending messages re-running `messages` would create — one per RECIPIENT, because
 * `scheduleMessageTool` fans a multi-contact message out into one document per contact. This is
 * the number the plan gate must be asked about, not the number of selected rows.
 */
export function scheduleAgainSlotCount(messages: UserMessage[]): number {
    return messages
        .filter(isScheduleAgainEligible)
        .reduce((total, m) => total + (m.contactList?.length ?? 0), 0);
}

/** What an HTTP status says about a media file. Only "not there" statuses count as gone: a 5xx, a
 *  429 or a network failure (status 0) says nothing about the file, so the copy is not blocked. */
export function probeResultFromStatus(status: number): AssetProbeResult {
    if (status >= 200 && status < 400) return 'ok';
    return status === 403 || status === 404 || status === 410 ? 'gone' : 'unknown';
}

export type ScheduleAgainOutcome =
    | { id: string; status: 'scheduled'; newId?: string }
    | { id: string; status: 'asset-unreachable' }
    | { id: string; status: 'past' }
    | { id: string; status: 'plan-limit' }
    | { id: string; status: 'ineligible' }
    | { id: string; status: 'failed'; error: string };

export interface ScheduleAgainDeps {
    now: Date;
    /** The idempotency key of the copy of `sentId` made for `day` — see `scheduleAgainIdempotencyKey`. */
    newKey: (sentId: string, day: Date) => string;
    probeAsset: (url: string) => Promise<AssetProbeResult>;
    /** Creates the pending message and resolves with its new id; rejects when it could not. */
    schedule: (draft: ScheduleAgainDraft) => Promise<string | undefined>;
}

/**
 * Re-run each sent message on `day`. One message's problem never stops the others: every input
 * gets exactly one outcome, in input order, so the caller can say precisely what happened to each.
 */
export async function runScheduleAgain(
    messages: UserMessage[],
    day: Date,
    deps: ScheduleAgainDeps,
): Promise<ScheduleAgainOutcome[]> {
    const outcomes: ScheduleAgainOutcome[] = [];
    for (const sent of messages) {
        outcomes.push(await runOne(sent, day, deps));
    }
    return outcomes;
}

async function runOne(sent: UserMessage, day: Date, deps: ScheduleAgainDeps): Promise<ScheduleAgainOutcome> {
    const id = sent._id;
    if (!isScheduleAgainEligible(sent)) return { id, status: 'ineligible' };

    const due = resolveScheduleAgainDueDate(sent.dueDate, day, deps.now);
    if (due === 'past') return { id, status: 'past' };

    try {
        if (sent.asset?.url && (await deps.probeAsset(sent.asset.url)) === 'gone') {
            return { id, status: 'asset-unreachable' };
        }
        const newId = await deps.schedule(buildScheduleAgainDraft(sent, due, deps.newKey(sent._id, day)));
        return { id, status: 'scheduled', newId };
    } catch (error) {
        return { id, status: 'failed', error: error instanceof Error ? error.message : String(error) };
    }
}

export type ScheduleAgainSkipReason = Exclude<ScheduleAgainOutcome['status'], 'scheduled'>;

export interface ScheduleAgainSummary {
    scheduledCount: number;
    /** Ids of the pending copies that were created, in input order. */
    newIds: string[];
    skipped: Array<{ id: string; reason: ScheduleAgainSkipReason }>;
}

export function summarizeScheduleAgain(outcomes: ScheduleAgainOutcome[]): ScheduleAgainSummary {
    const summary: ScheduleAgainSummary = { scheduledCount: 0, newIds: [], skipped: [] };
    for (const outcome of outcomes) {
        if (outcome.status === 'scheduled') {
            summary.scheduledCount += 1;
            if (outcome.newId) summary.newIds.push(outcome.newId);
        } else {
            summary.skipped.push({ id: outcome.id, reason: outcome.status });
        }
    }
    return summary;
}

/** Keep only the selected ids that are still selectable (a filter or a refresh can remove rows). */
export function pruneSelection(selected: ReadonlySet<string>, selectable: readonly string[]): ReadonlySet<string> {
    const allowed = new Set(selectable);
    const kept = [...selected].filter(id => allowed.has(id));
    return kept.length === selected.size ? selected : new Set(kept);
}

/** Ids of the messages that would land in the past if re-run on `day` (the dialog blocks on these). */
export function messagesLandingInThePast(messages: UserMessage[], day: Date, now: Date): string[] {
    return messages
        .filter(m => resolveScheduleAgainDueDate(m.dueDate, day, now) === 'past')
        .map(m => m._id);
}
